import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { useToast } from '@/shared/components/Toast';
import { useDeleteManualReferral } from '../../hooks/useDeleteManualReferral';

export type DeleteManualReferralTarget = {
  id: string;
  referrerName: string;
  referredName: string;
  careSeekerName?: string | null;
  /** ISO timestamp of the referral date. */
  referralDate: string;
};

type DeleteManualReferralDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referral: DeleteManualReferralTarget;
  onDeleted?: () => void;
};

const REASON_MAX_LENGTH = 500;

const formatReferralDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Feature 117: controlled confirm-before-delete for one manual referral. */
export function DeleteManualReferralDialog({
  open,
  onOpenChange,
  referral,
  onDeleted,
}: DeleteManualReferralDialogProps) {
  const { showToast } = useToast();
  const deleteReferral = useDeleteManualReferral();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open, referral.id]);

  const handleDelete = async (event: React.MouseEvent) => {
    // Keep the dialog open until the request settles.
    event.preventDefault();
    try {
      await deleteReferral.mutateAsync({ id: referral.id, reason });
      showToast('Referral deleted.', 'success');
      onDeleted?.();
      onOpenChange(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not delete the referral.', 'error');
    }
  };

  const forCareSeeker = referral.careSeekerName ? `, for ${referral.careSeekerName}` : '';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete referral?</AlertDialogTitle>
          <AlertDialogDescription>
            {`${referral.referrerName} → ${referral.referredName}${forCareSeeker} on ${formatReferralDate(referral.referralDate)}. It disappears from the Referrals tab and from metrics. Pending follow-up emails are cancelled. Emails already sent are not recalled. This can't be undone from the app.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="delete-referral-reason">Reason (optional)</Label>
          <Textarea
            id="delete-referral-reason"
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Kept in the audit log only"
            disabled={deleteReferral.isPending}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteReferral.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteReferral.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete referral
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
